/**
 * Admin hooks for Gender Party management
 */

import { useQuery, useMutation } from "urql";
import { useCallback } from "react";

// =============================================================================
// GraphQL Documents
// =============================================================================

const ADMIN_GENDER_PARTIES_QUERY = `
  query AdminGenderParties($eventId: ID) {
    adminGenderParties(eventId: $eventId) {
      id
      eventId
      event {
        id
        title
      }
      name
      description
      status
      partnerOneName
      partnerTwoName
      couplePhotoUrl
      allowVoteChange
      showResultsMode
      answer
      isTest
      createdAt
      updatedAt
      photos {
        id
        imageUrl
        caption
        sortOrder
      }
      voteCounts {
        boyVotes
        girlVotes
        totalVotes
        boyPercentage
        girlPercentage
      }
    }
  }
`;

const ADMIN_GENDER_PARTY_QUERY = `
  query AdminGenderParty($id: ID!) {
    adminGenderParty(id: $id) {
      id
      eventId
      event {
        id
        title
      }
      name
      description
      status
      partnerOneName
      partnerTwoName
      couplePhotoUrl
      allowVoteChange
      showResultsMode
      answer
      isTest
      createdAt
      updatedAt
      photos {
        id
        imageUrl
        caption
        sortOrder
      }
      voteCounts {
        boyVotes
        girlVotes
        totalVotes
        boyPercentage
        girlPercentage
      }
    }
  }
`;

const ADMIN_GENDER_PARTY_EVENTS_QUERY = `
  query AdminGenderPartyEvents {
    adminGenderPartyEvents {
      id
      title
      coverImage
      startDate
      endDate
      genderPartiesCount
      activeGenderPartiesCount
    }
  }
`;

const CREATE_GENDER_PARTY_MUTATION = `
  mutation AdminCreateGenderParty($input: GenderPartyInput!) {
    adminCreateGenderParty(input: $input) {
      id
    }
  }
`;

const UPDATE_GENDER_PARTY_MUTATION = `
  mutation AdminUpdateGenderParty($id: ID!, $input: GenderPartyUpdateInput!) {
    adminUpdateGenderParty(id: $id, input: $input) {
      id
    }
  }
`;

const DELETE_GENDER_PARTY_MUTATION = `
  mutation AdminDeleteGenderParty($id: ID!) {
    adminDeleteGenderParty(id: $id)
  }
`;

const OPEN_VOTING_MUTATION = `
  mutation AdminOpenGenderPartyVoting($id: ID!) {
    adminOpenGenderPartyVoting(id: $id) {
      id
      status
    }
  }
`;

const CLOSE_VOTING_MUTATION = `
  mutation AdminCloseGenderPartyVoting($id: ID!) {
    adminCloseGenderPartyVoting(id: $id) {
      id
      status
    }
  }
`;

const REVEAL_RESULTS_MUTATION = `
  mutation AdminRevealGenderPartyResults($id: ID!) {
    adminRevealGenderPartyResults(id: $id) {
      id
      status
    }
  }
`;

const REVEAL_ANSWER_MUTATION = `
  mutation AdminRevealGenderPartyAnswer($id: ID!) {
    adminRevealGenderPartyAnswer(id: $id) {
      id
      status
    }
  }
`;

const RESET_TEST_MUTATION = `
  mutation AdminResetTestGenderParty($id: ID!) {
    adminResetTestGenderParty(id: $id) {
      id
      status
    }
  }
`;

const ADD_PHOTO_MUTATION = `
  mutation AdminAddGenderPartyPhoto($genderPartyId: ID!, $input: GenderPartyPhotoInput!) {
    adminAddGenderPartyPhoto(genderPartyId: $genderPartyId, input: $input) {
      id
      imageUrl
      caption
      sortOrder
    }
  }
`;

const DELETE_PHOTO_MUTATION = `
  mutation AdminDeleteGenderPartyPhoto($id: ID!) {
    adminDeleteGenderPartyPhoto(id: $id)
  }
`;

// =============================================================================
// Types
// =============================================================================

export type GenderPartyStatus =
  | "DRAFT"
  | "VOTING_OPEN"
  | "VOTING_CLOSED"
  | "REVEALED"
  | "COMPLETED";

export type GenderVote = "BOY" | "GIRL";

export type ShowResultsMode = "ALWAYS" | "AFTER_VOTE" | "ON_REVEAL";

export interface GenderPartyPhoto {
  id: string;
  imageUrl: string;
  caption: string | null;
  sortOrder: number;
}

export interface GenderPartyVoteCounts {
  boyVotes: number;
  girlVotes: number;
  totalVotes: number;
  boyPercentage: number;
  girlPercentage: number;
}

export interface GenderParty {
  id: string;
  eventId: string | null;
  event: { id: string; title: string } | null;
  name: string;
  description: string | null;
  status: GenderPartyStatus;
  partnerOneName: string;
  partnerTwoName: string;
  couplePhotoUrl: string | null;
  allowVoteChange: boolean;
  showResultsMode: ShowResultsMode;
  answer: GenderVote | null;
  isTest: boolean;
  createdAt: string;
  updatedAt: string;
  photos: GenderPartyPhoto[];
  voteCounts: GenderPartyVoteCounts | null;
}

export interface GenderPartyEventSummary {
  id: string;
  title: string;
  coverImage: string | null;
  startDate: string | null;
  endDate: string | null;
  genderPartiesCount: number;
  activeGenderPartiesCount: number;
}

export interface GenderPartyInput {
  name: string;
  description?: string | null;
  eventId?: string | null;
  partnerOneName: string;
  partnerTwoName: string;
  couplePhotoUrl?: string | null;
  allowVoteChange?: boolean;
  showResultsMode?: ShowResultsMode;
  answer?: GenderVote | null;
  isTest?: boolean;
}

// =============================================================================
// Hooks
// =============================================================================

function extractGraphQLErrorMessage(error: Error, defaultMessage: string): string {
  if (error.message.includes("GraphQL")) {
    const match = error.message.match(/\[GraphQL\] (.+)/);
    if (match) return match[1];
  }
  return error.message || defaultMessage;
}

export function useAdminGenderPartyEvents() {
  const [result, reexecuteQuery] = useQuery<{
    adminGenderPartyEvents: GenderPartyEventSummary[];
  }>({
    query: ADMIN_GENDER_PARTY_EVENTS_QUERY,
  });

  return {
    events: result.data?.adminGenderPartyEvents ?? [],
    isLoading: result.fetching,
    error: result.error ?? null,
    refetch: () => reexecuteQuery({ requestPolicy: "network-only" }),
  };
}

export function useAdminGenderParties(eventId?: string | null) {
  const [result, reexecuteQuery] = useQuery<{
    adminGenderParties: GenderParty[];
  }>({
    query: ADMIN_GENDER_PARTIES_QUERY,
    variables: { eventId },
  });

  return {
    genderParties: result.data?.adminGenderParties ?? [],
    isLoading: result.fetching,
    error: result.error
      ? extractGraphQLErrorMessage(result.error, "Ошибка загрузки")
      : null,
    refetch: () => reexecuteQuery({ requestPolicy: "network-only" }),
  };
}

export function useAdminGenderParty(id?: string) {
  const [result, reexecuteQuery] = useQuery<{
    adminGenderParty: GenderParty | null;
  }>({
    query: ADMIN_GENDER_PARTY_QUERY,
    variables: { id },
    pause: !id,
  });

  return {
    genderParty: result.data?.adminGenderParty ?? null,
    isLoading: result.fetching,
    error: result.error
      ? extractGraphQLErrorMessage(result.error, "Ошибка загрузки")
      : null,
    refetch: () => reexecuteQuery({ requestPolicy: "network-only" }),
  };
}

export function useGenderPartyMutations() {
  const [, executeCreate] = useMutation(CREATE_GENDER_PARTY_MUTATION);
  const [, executeUpdate] = useMutation(UPDATE_GENDER_PARTY_MUTATION);
  const [, executeDelete] = useMutation(DELETE_GENDER_PARTY_MUTATION);
  const [, executeOpenVoting] = useMutation(OPEN_VOTING_MUTATION);
  const [, executeCloseVoting] = useMutation(CLOSE_VOTING_MUTATION);
  const [, executeRevealResults] = useMutation(REVEAL_RESULTS_MUTATION);
  const [, executeRevealAnswer] = useMutation(REVEAL_ANSWER_MUTATION);
  const [, executeResetTest] = useMutation(RESET_TEST_MUTATION);
  const [, executeAddPhoto] = useMutation(ADD_PHOTO_MUTATION);
  const [, executeDeletePhoto] = useMutation(DELETE_PHOTO_MUTATION);

  const create = useCallback(
    async (input: GenderPartyInput) => {
      const result = await executeCreate({ input });
      if (result.error) {
        throw new Error(
          extractGraphQLErrorMessage(result.error, "Ошибка создания")
        );
      }
      return result.data?.adminCreateGenderParty;
    },
    [executeCreate]
  );

  const update = useCallback(
    async (id: string, input: Partial<GenderPartyInput>) => {
      const result = await executeUpdate({ id, input });
      if (result.error) {
        throw new Error(
          extractGraphQLErrorMessage(result.error, "Ошибка обновления")
        );
      }
      return result.data?.adminUpdateGenderParty;
    },
    [executeUpdate]
  );

  const remove = useCallback(
    async (id: string) => {
      const result = await executeDelete({ id });
      if (result.error) {
        throw new Error(
          extractGraphQLErrorMessage(result.error, "Ошибка удаления")
        );
      }
      return result.data?.adminDeleteGenderParty;
    },
    [executeDelete]
  );

  const openVoting = useCallback(
    async (id: string) => {
      const result = await executeOpenVoting({ id });
      if (result.error) {
        throw new Error(
          extractGraphQLErrorMessage(result.error, "Ошибка открытия голосования")
        );
      }
      return result.data?.adminOpenGenderPartyVoting;
    },
    [executeOpenVoting]
  );

  const closeVoting = useCallback(
    async (id: string) => {
      const result = await executeCloseVoting({ id });
      if (result.error) {
        throw new Error(
          extractGraphQLErrorMessage(result.error, "Ошибка закрытия голосования")
        );
      }
      return result.data?.adminCloseGenderPartyVoting;
    },
    [executeCloseVoting]
  );

  const revealResults = useCallback(
    async (id: string) => {
      const result = await executeRevealResults({ id });
      if (result.error) {
        throw new Error(
          extractGraphQLErrorMessage(result.error, "Ошибка раскрытия результатов")
        );
      }
      return result.data?.adminRevealGenderPartyResults;
    },
    [executeRevealResults]
  );

  const revealAnswer = useCallback(
    async (id: string) => {
      const result = await executeRevealAnswer({ id });
      if (result.error) {
        throw new Error(
          extractGraphQLErrorMessage(result.error, "Ошибка раскрытия ответа")
        );
      }
      return result.data?.adminRevealGenderPartyAnswer;
    },
    [executeRevealAnswer]
  );

  const resetTest = useCallback(
    async (id: string) => {
      const result = await executeResetTest({ id });
      if (result.error) {
        throw new Error(
          extractGraphQLErrorMessage(result.error, "Ошибка сброса")
        );
      }
      return result.data?.adminResetTestGenderParty;
    },
    [executeResetTest]
  );

  const addPhoto = useCallback(
    async (genderPartyId: string, imageUrl: string, caption?: string | null) => {
      const result = await executeAddPhoto({
        genderPartyId,
        input: { imageUrl, caption },
      });
      if (result.error) {
        throw new Error(
          extractGraphQLErrorMessage(result.error, "Ошибка добавления фото")
        );
      }
      return result.data?.adminAddGenderPartyPhoto;
    },
    [executeAddPhoto]
  );

  const deletePhoto = useCallback(
    async (id: string) => {
      const result = await executeDeletePhoto({ id });
      if (result.error) {
        throw new Error(
          extractGraphQLErrorMessage(result.error, "Ошибка удаления фото")
        );
      }
      return result.data?.adminDeleteGenderPartyPhoto;
    },
    [executeDeletePhoto]
  );

  return {
    create,
    update,
    remove,
    openVoting,
    closeVoting,
    revealResults,
    revealAnswer,
    resetTest,
    addPhoto,
    deletePhoto,
  };
}
